import { useMemo } from "react";
import GenericForm from "../GenericForm";
import { ApiClient } from "@/api/ApiClient";
import { buildAdminTenantUserFormConfig } from "./form";
import { AdminTenantUserFormData } from "@/formTypes/AdminTenantUserFormDataType";
import { refreshSessionIfCurrentUser } from "@utils/Auth";

type AdminTenantUserFormProps = {
    formId: string;
    /** tenantId objetivo (owner de la suscripcion) */
    targetTenantId: string;
    /** roles del tenant objetivo como { id: name } */
    roles: Record<string, string>;
    /** datos previos en modo edicion (incluye id) */
    defaultData?: AdminTenantUserFormData;
    onStored?: () => void;
};

const AdminTenantUserForm = (props: AdminTenantUserFormProps) => {
    const { formId, targetTenantId, roles, defaultData, onStored } = props;
    const isEdit = !!defaultData?.id;

    const formConfig = useMemo(
        () => buildAdminTenantUserFormConfig(roles, isEdit),
        [roles, isEdit]
    );

    const createFn = async (data: AdminTenantUserFormData): Promise<AdminTenantUserFormData> => {
        const { data: res } = await ApiClient.mutations.adminTenantUserMutation({
            action: "create",
            tenantId: targetTenantId,
            email: `${data.email}`,
            name: `${data.name}`,
            roleId: `${data.roleId}`,
            password: data.password ? `${data.password}` : null,
            sendInvite: !!data.sendInvite,
        });
        if (!res?.ok) {
            throw Error(res?.error || "No fue posible crear el usuario");
        }
        return { ...data, id: res.user?.id };
    };

    const updateFn = async (id: string, data: AdminTenantUserFormData): Promise<AdminTenantUserFormData> => {
        const { data: res } = await ApiClient.mutations.adminTenantUserMutation({
            action: "update",
            id,
            username: `${data.email}`,
            email: `${data.email}`,
            name: `${data.name}`,
            roleId: `${data.roleId}`,
        });
        if (!res?.ok) {
            throw Error(res?.error || "No fue posible actualizar el usuario");
        }
        await refreshSessionIfCurrentUser(`${data.email}`);
        return data;
    };

    return (
        <GenericForm<AdminTenantUserFormData, AdminTenantUserFormData>
            formId={formId}
            formConfig={formConfig}
            defaultData={defaultData}
            createFn={createFn}
            updateFn={updateFn}
            onStored={onStored}
            successMessage={isEdit ? "El usuario ha sido actualizado correctamente" : "El usuario ha sido creado correctamente"}
            errorMessage="No fue posible almacenar la información del usuario"
        />
    );
};

export default AdminTenantUserForm;
